import * as React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { EVIDENCE_KIND_LABELS, RangeSource } from '../../models';
import '../../containers/my-ranges.css';

/**
 * Why a source has no link, when that is worth saying.
 *
 * It is worth saying for a range LabSmarts ships: a practitioner who has been
 * told every number has a source and then finds one with nothing to click will
 * assume the link is broken, and "calculated" or "it is a book" is the answer.
 *
 * It is NOT worth saying for a practitioner's own note. They know whether they
 * had a PubMed ID, they chose not to enter one, and a line explaining their own
 * blank field back to them reads as a reproach. An expert-opinion source with
 * no citation is theirs — ours carries the ISBN there.
 */
function noLinkReason(source: RangeSource): string {
  if (source.kind === 'derived') {
    return 'Calculated by LabSmarts, so there is no paper behind it.';
  }
  if (source.kind === 'expert-opinion' && !source.citation) {
    return '';
  }
  return 'A book rather than a paper, so there is nothing to link to. The ISBN is above.';
}

/**
 * Where one number comes from. See my-ranges-spec.md §6.2.
 *
 * One component for every place it is shown — the popover behind a number's
 * chip in My Ranges, the Sources panel, and a custom marker's own
 * source in My Markers — so they cannot drift into saying the same thing
 * differently. The third caller is why this is a component of its own rather
 * than a function inside the My Ranges container: a settings page importing a
 * container to borrow a card is backwards.
 *
 * The order is the argument, not a layout: what kind of claim this is, then
 * what the number IS in one sentence, then the paper and the cohort, then a
 * second method agreeing, then the number the reader is about to object with.
 * A practitioner who has taken the courses arrives already holding a different
 * figure; naming it before they raise it is the difference between a
 * defensible range and an argument.
 */
export const RangeSourceCard: React.FC<{
  source: RangeSource;
  /** "Lab Min · 30 ng/mL". Omitted in the panel, which has its own heading. */
  heading?: string;
}> = ({ source, heading }) => (
  <div
    className={
      'my-ranges-source-card' + (heading ? '' : ' my-ranges-source-card-inline')
    }
  >
    {/* With no heading the head holds only the tier pill, and left as a row of
        its own it costs a line of height and pushes the claim below the bound
        label it should start level with. Floated instead, so the claim begins
        at the top and the pill sits out at the right of it. */}
    <div className="my-ranges-source-head">
      {heading ? <span className="my-ranges-source-title">{heading}</span> : null}
      <span className={`my-ranges-source-kind my-ranges-kind-${source.kind}`}>
        {EVIDENCE_KIND_LABELS[source.kind]}
      </span>
    </div>
    <p className="my-ranges-source-claim">{source.claim}</p>
    {/* A practitioner's own note has no citation line: their sentence is the
        citation, and an empty <p> renders as a gap rather than as nothing. */}
    {source.citation ? (
      <p className="my-ranges-source-citation">{source.citation}</p>
    ) : null}
    {source.detail ? (
      <p className="my-ranges-source-detail">{source.detail}</p>
    ) : null}
    {/* Two unrelated methods landing on the same number is the strongest
        thing a range can show, and it is wasted if it reads as one citation
        run on. Hence the rule down its left. */}
    {source.corroboration ? (
      <div className="my-ranges-source-second">
        <p className="my-ranges-source-citation">
          {source.corroboration.citation}
        </p>
        {source.corroboration.detail ? (
          <p className="my-ranges-source-detail">{source.corroboration.detail}</p>
        ) : null}
      </div>
    ) : null}
    {source.counterpoint ? (
      <div className="my-ranges-source-counter">
        <p className="my-ranges-source-counter-q">
          {source.counterpoint.question}
        </p>
        <p className="my-ranges-source-counter-a">{source.counterpoint.answer}</p>
      </div>
    ) : null}
    {(source.links || []).length > 0 ? (
      <div className="my-ranges-source-links">
        {source.links!.map(link => (
          <a
            key={link.href}
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {link.label}{' '}
            <FontAwesomeIcon icon="external-link-alt" style={{ fontSize: '9px' }} />
          </a>
        ))}
      </div>
    ) : noLinkReason(source) ? (
      <div className="my-ranges-source-nolink">{noLinkReason(source)}</div>
    ) : null}
  </div>
);
